import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const QUESTION_TYPES = ["easy", "medium", "hard"];
const APPROVAL_STATUSES = ["approved", "pending", "rejected"];
const ANSWER_OPTIONS = ["option1", "option2", "option3", "option4"];

function UpdateArQuestion() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [redirect, setRedirect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    color: "success",
    message: "",
  });
  const [form, setForm] = useState({
    subject_id: "",
    topic_id: "",
    level_id: "",
    question_type: "easy",
    approved_status: "pending",
    assertion: "",
    reason: "",
    assertion_hindi: "",
    reason_hindi: "",
    option1: "",
    option1_hindi: "",
    option2: "",
    option2_hindi: "",
    option3: "",
    option3_hindi: "",
    option4: "",
    option4_hindi: "",
    answer: "option1",
    description: "",
    description_hindi: "",
    time: 60,
    language_converted: false,
  });

  const showNotice = (message, color = "success", shouldRedirect = false) => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification({ open: false, color: "success", message: "" });
      if (shouldRedirect) {
        setRedirect(true);
      }
    }, 2500);
  };

  const handleField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const loadSubjects = async () => {
    const resp = await axios.get(`${Constants.getUrls.getAllSubjects}?search=&limit=1000&page=1`);
    setAllSubjects(resp?.data?.data?.docs || []);
  };

  const loadTopics = async (subjectId, preselectedTopicId = "") => {
    if (!subjectId) {
      setAllTopics([]);
      return;
    }

    const resp = await axios.get(`${Constants.getUrls.getAllTopics}?subject_id=${subjectId}`);
    const topics = resp?.data?.data?.docs || [];
    setAllTopics(topics);
    if (preselectedTopicId) {
      setForm((prev) => ({ ...prev, topic_id: preselectedTopicId }));
    }
  };

  const loadLevels = async (subjectId, topicId, preselectedLevelId = "") => {
    if (!subjectId || !topicId) {
      setAllLevels([]);
      return;
    }

    const resp = await axios.get(
      `${Constants.getUrls.getAllArLevels}?subject_id=${subjectId}&topic_id=${topicId}`
    );
    const levels = resp?.data?.data?.docs || [];
    setAllLevels(levels);
    if (preselectedLevelId) {
      setForm((prev) => ({ ...prev, level_id: preselectedLevelId }));
    }
  };

  const loadSingleQuestion = async () => {
    const resp = await axios.get(`${Constants.getUrls.getSingleArQuestion}${id}`);
    const data = resp?.data?.data || {};
    const nextForm = {
      subject_id: data?.subject_id?._id || data?.subject_id || "",
      topic_id: data?.topic_id?._id || data?.topic_id || "",
      level_id: data?.level_id?._id || data?.level_id || "",
      question_type: data?.question_type || "easy",
      approved_status: data?.approved_status || "pending",
      assertion: data?.assertion || "",
      reason: data?.reason || "",
      assertion_hindi: data?.assertion_hindi || "",
      reason_hindi: data?.reason_hindi || "",
      option1: data?.option1 || "",
      option1_hindi: data?.option1_hindi || "",
      option2: data?.option2 || "",
      option2_hindi: data?.option2_hindi || "",
      option3: data?.option3 || "",
      option3_hindi: data?.option3_hindi || "",
      option4: data?.option4 || "",
      option4_hindi: data?.option4_hindi || "",
      answer: data?.answer || "option1",
      description: data?.description || "",
      description_hindi: data?.description_hindi || "",
      time: data?.time ?? 60,
      language_converted: Boolean(data?.language_converted),
    };

    setForm(nextForm);
    await loadTopics(nextForm.subject_id, nextForm.topic_id);
    await loadLevels(nextForm.subject_id, nextForm.topic_id, nextForm.level_id);
  };

  useEffect(() => {
    const boot = async () => {
      try {
        if (init() !== "success") {
          window.location.assign("/signin");
          return;
        }

        setLoading(true);
        await loadSubjects();
        await loadSingleQuestion();
      } catch (error) {
        showNotice(error?.response?.data?.message || "Unable to load AR question.", "error");
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, [id]);

  const handleSubjectChange = async (value) => {
    setForm((prev) => ({
      ...prev,
      subject_id: value,
      topic_id: "",
      level_id: "",
    }));
    setAllLevels([]);
    await loadTopics(value);
  };

  const handleTopicChange = async (value) => {
    setForm((prev) => ({
      ...prev,
      topic_id: value,
      level_id: "",
    }));
    await loadLevels(form.subject_id, value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      await axios.put(`${Constants.putUrls.updateArQuestion}${id}`, {
        ...form,
        time: Number(form.time || 60),
      });
      showNotice("AR question updated successfully.", "success", true);
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to update AR question.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (redirect) {
    return <Navigate to="/ar-questions" replace />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Update AR Question
            </MDTypography>
          </MDBox>

          <MDBox py={3} px={2}>
            {loading ? (
              <MDTypography variant="button" color="text">
                Loading AR question...
              </MDTypography>
            ) : (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Select Subject</InputLabel>
                    <Select
                      value={form.subject_id}
                      onChange={(event) => handleSubjectChange(event.target.value)}
                      required
                      style={{ width: "100%", height: "45px", color: "#111" }}
                    >
                      <MenuItem value="">Select Subject</MenuItem>
                      {allSubjects.map((item) => (
                        <MenuItem key={item._id} value={item._id}>
                          {item.subject_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Select Topic</InputLabel>
                    <Select
                      value={form.topic_id}
                      onChange={(event) => handleTopicChange(event.target.value)}
                      required
                      style={{ width: "100%", height: "45px", color: "#111" }}
                    >
                      <MenuItem value="">Select Topic</MenuItem>
                      {allTopics.map((item) => (
                        <MenuItem key={item._id} value={item._id}>
                          {item.topic_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Select AR Level</InputLabel>
                    <Select
                      value={form.level_id}
                      onChange={(event) => handleField("level_id", event.target.value)}
                      required
                      style={{ width: "100%", height: "45px", color: "#111" }}
                    >
                      <MenuItem value="">Select AR Level</MenuItem>
                      {allLevels.map((item) => (
                        <MenuItem key={item._id} value={item._id}>
                          {item.level_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <InputLabel>Question Type</InputLabel>
                    <Select
                      value={form.question_type}
                      onChange={(event) => handleField("question_type", event.target.value)}
                      required
                      style={{ width: "100%", height: "45px", color: "#111" }}
                    >
                      {QUESTION_TYPES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Approval Status</InputLabel>
                    <Select
                      value={form.approved_status}
                      onChange={(event) => handleField("approved_status", event.target.value)}
                      required
                      style={{ width: "100%", height: "45px", color: "#111" }}
                    >
                      {APPROVAL_STATUSES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Time (seconds)"
                      value={form.time}
                      onChange={(event) => handleField("time", event.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Assertion"
                      value={form.assertion}
                      onChange={(event) => handleField("assertion", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Reason"
                      value={form.reason}
                      onChange={(event) => handleField("reason", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Assertion Hindi"
                      value={form.assertion_hindi}
                      onChange={(event) => handleField("assertion_hindi", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Reason Hindi"
                      value={form.reason_hindi}
                      onChange={(event) => handleField("reason_hindi", event.target.value)}
                    />
                  </Grid>

                  {["1", "2", "3", "4"].map((item) => (
                    <React.Fragment key={item}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={`Option ${item}`}
                          value={form[`option${item}`]}
                          onChange={(event) => handleField(`option${item}`, event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={`Option ${item} Hindi`}
                          value={form[`option${item}_hindi`]}
                          onChange={(event) =>
                            handleField(`option${item}_hindi`, event.target.value)
                          }
                        />
                      </Grid>
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} md={6}>
                    <InputLabel>Correct Answer</InputLabel>
                    <Select
                      value={form.answer}
                      onChange={(event) => handleField("answer", event.target.value)}
                      required
                      style={{ width: "100%", height: "45px", color: "#111" }}
                    >
                      {ANSWER_OPTIONS.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={6} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(form.language_converted)}
                          onChange={(event) =>
                            handleField("language_converted", event.target.checked)
                          }
                        />
                      }
                      label="Language Converted"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Description"
                      value={form.description}
                      onChange={(event) => handleField("description", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Description Hindi"
                      value={form.description_hindi}
                      onChange={(event) => handleField("description_hindi", event.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <MDBox display="flex" gap={1.5} flexWrap="wrap">
                      <MDButton type="submit" variant="contained" color="info" disabled={saving}>
                        {saving ? "Updating..." : "Update AR Question"}
                      </MDButton>
                      <MDButton
                        type="button"
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/ar-questions")}
                      >
                        Cancel
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </form>
            )}
          </MDBox>

          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime=""
            open={notification.open}
            onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
            close={() => setNotification((prev) => ({ ...prev, open: false }))}
            bgWhite
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default UpdateArQuestion;
